import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { IntlContext } from '../../../../contexts/IntlContext/IntlContext'
import { getPlansBaseValues } from '../../../../services/planService'
import { getAddonPackages } from '../../../../services/stripeService'
import { getActiveSubscription } from '../../../../services/subscriptionService'
import {
  UPSELL_CAMPAIGN_PACKAGE_CODE,
  UPSELL_FEATURED_PACKAGE_CODE,
  UPSELL_FEATURED_PLAN_TIER,
  buildFeaturedPackageOffer,
  buildFeaturedPlanOffer,
  buildRechargeFeaturedOffer,
  buildUpgradeFeaturedOffer,
  formatUpsellPrice,
  getCurrentPlanTier,
  getFeaturedUpgradePlan,
  getSelectablePlans,
  getPackageByCode,
  getPlanByTier,
  mapPackagesToOptions,
  mapPlansToOptions,
  mapUpgradePlansToOptions,
} from './upsellCatalog'

export function useUpsellCatalog({ enabled = true } = {}) {
  const { country } = useContext(IntlContext)
  const [dbPlans, setDbPlans] = useState([])
  const [dbPackages, setDbPackages] = useState([])
  const [currentPlanTier, setCurrentPlanTier] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false

    const fetchSubscription = async () => {
      try {
        const response = await getActiveSubscription()
        if (cancelled) return

        setCurrentPlanTier(getCurrentPlanTier(response?.data))
      } catch (error) {
        if (!cancelled) {
          setCurrentPlanTier(0)
        }
      }
    }

    fetchSubscription()

    return () => {
      cancelled = true
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return undefined

    let cancelled = false

    const fetchCatalog = async () => {
      setIsLoading(true)

      try {
        const language = country === 'br' ? 'ptBR' : 'en'
        const [plansResponse, packagesResponse] = await Promise.all([
          getPlansBaseValues(language),
          getAddonPackages(language),
        ])

        if (!cancelled) {
          if (plansResponse?.data) {
            setDbPlans(plansResponse.data)
          }

          if (packagesResponse?.data) {
            setDbPackages(packagesResponse.data)
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchCatalog()

    return () => {
      cancelled = true
    }
  }, [country, enabled])

  const featuredPlan = useMemo(
    () => getPlanByTier(dbPlans, UPSELL_FEATURED_PLAN_TIER),
    [dbPlans],
  )

  const featuredUpgradePlan = useMemo(
    () => getFeaturedUpgradePlan(dbPlans, currentPlanTier),
    [dbPlans, currentPlanTier],
  )

  const hasUpgradePlans = Boolean(featuredUpgradePlan)

  const hasSelectablePlans = useMemo(() => {
    if (!dbPlans.length) {
      return true
    }

    return getSelectablePlans(dbPlans, currentPlanTier).length > 0
  }, [dbPlans, currentPlanTier])

  const featuredPackage = useMemo(
    () => getPackageByCode(UPSELL_FEATURED_PACKAGE_CODE, dbPackages),
    [dbPackages],
  )

  const campaignPackage = useMemo(
    () => getPackageByCode(UPSELL_CAMPAIGN_PACKAGE_CODE, dbPackages),
    [dbPackages],
  )

  const getPlanOptions = useCallback(
    (excludeTiers = []) =>
      mapPlansToOptions(dbPlans, {
        country,
        excludeTiers,
      }),
    [country, dbPlans],
  )

  const getPackageOptions = useCallback(
    (excludeCodes = []) =>
      mapPackagesToOptions({
        country,
        excludeCodes,
        packages: dbPackages,
      }),
    [country, dbPackages],
  )

  const getUpgradePlanOptions = useCallback(() => {
    const featuredTier = featuredUpgradePlan?.tier

    return mapUpgradePlansToOptions(dbPlans, currentPlanTier, {
      country,
      excludeTiers: featuredTier ? [featuredTier] : [],
    })
  }, [country, currentPlanTier, dbPlans, featuredUpgradePlan])

  return {
    country,
    dbPlans,
    dbPackages,
    currentPlanTier,
    hasUpgradePlans,
    hasSelectablePlans,
    isLoading,
    featuredPlan,
    featuredUpgradePlan,
    featuredPackage,
    campaignPackage,
    formatPrice: (price) => formatUpsellPrice(price, country),
    getPlanOptions,
    getUpgradePlanOptions,
    getPackageOptions,
    buildFeaturedPlanOffer: (plan = featuredUpgradePlan ?? featuredPlan) =>
      buildFeaturedPlanOffer(plan, country),
    buildFeaturedPackageOffer: (pkg, options) =>
      buildFeaturedPackageOffer(pkg, country, options),
    buildRechargeFeaturedOffer: (pkg = featuredPackage) =>
      buildRechargeFeaturedOffer(pkg, country),
    buildUpgradeFeaturedOffer: (plan = featuredUpgradePlan ?? featuredPlan) =>
      buildUpgradeFeaturedOffer(plan, country),
  }
}

export function useUpgradePlansTab(
  catalog,
  activeTab,
  setActiveTab,
  tabs,
) {
  const availableTabs = useMemo(
    () =>
      catalog.hasSelectablePlans
        ? tabs
        : tabs.filter((tab) => tab.id !== 'plans'),
    [catalog.hasSelectablePlans, tabs],
  )

  useEffect(() => {
    if (!catalog.hasSelectablePlans && activeTab === 'plans') {
      setActiveTab('packages')
    }
  }, [activeTab, catalog.hasSelectablePlans, setActiveTab])

  return availableTabs
}
